<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import * as yup from 'yup'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { ref } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Form, type GenericObject } from 'vee-validate'
  import {
    type StripeInvoiceExportRequestRequest,
    type StripeInvoiceExportResponse,
    StripeInvoicesService
  } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      month: 'Month',
      year: 'Year',
      startExport: 'Start export',
      processing: 'Processing..',
      download: 'Download'
    },
    de: {
      month: 'Monat',
      year: 'Jahr',
      startExport: 'Export starten',
      processing: 'Lade..',
      download: 'Herunterladen'
    }
  })

  const exportOptions = ref<StripeInvoiceExportRequestRequest>({ month: 1, year: 2024 })

  const monthOptions = [
    { value: 1, title: 'January' },
    { value: 2, title: 'February' },
    { value: 3, title: 'March' },
    { value: 4, title: 'April' },
    { value: 5, title: 'May' },
    { value: 6, title: 'June' },
    { value: 7, title: 'July' },
    { value: 8, title: 'August' },
    { value: 9, title: 'September' },
    { value: 10, title: 'October' },
    { value: 11, title: 'November' },
    { value: 12, title: 'December' }
  ]

  const yearOptions = [
    { value: 2021, title: '2021' },
    { value: 2022, title: '2022' },
    { value: 2023, title: '2023' },
    { value: 2024, title: '2024' },
    { value: 2025, title: '2025' },
    { value: 2026, title: '2026' },
    { value: 2027, title: '2027' },
    { value: 2028, title: '2028' },
    { value: 2029, title: '2029' },
    { value: 2030, title: '2030' }
  ]

  const isProcessing = ref(false)
  const exportResponse = ref<StripeInvoiceExportResponse | null>(null)

  const handleSubmit = async (values: GenericObject) => {
    isProcessing.value = true
    StripeInvoicesService.stripeInvoicesExportCreate({
      requestBody: values as StripeInvoiceExportRequestRequest
    }).then((response) => {
      exportResponse.value = response
    })
  }
</script>
<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Invoices', to: ROUTES.ADMIN.PAYMENTS.INVOICES_LIST },
            {
              title: `Export`,
              to: ROUTES.ADMIN.PAYMENTS.INVOICES_EXPORT
            }
          ]"
        />
      </div>
    </div>
    <div class="p-4 pt-5">
      <div
        class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="w-full md:w-1/2">
          <Form
            v-slot="{ errors }"
            @submit="handleSubmit"
          >
            <SelectField
              v-model="exportOptions.month"
              :label="t('month')"
              :is-filterable="true"
              name="month"
              :rules="yup.number().required()"
              :options="monthOptions"
            />
            <SelectField
              v-model="exportOptions.year"
              :label="t('year')"
              :is-filterable="true"
              name="year"
              :rules="yup.number().required()"
              :options="yearOptions"
            />
            <button
              v-if="exportResponse === null"
              class="mt-3 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
                text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
                focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
              type="submit"
              :disabled="Object.keys(errors).length > 0 || isProcessing"
            >
              <div
                v-if="isProcessing"
                class="inline-block size-4 animate-spin rounded-full border-[3px] border-current border-t-transparent
                  text-gray-200"
                role="status"
                aria-label="loading"
              >
                <span class="sr-only">Loading...</span>
              </div>
              {{ isProcessing ? t('processing') : t('startExport') }}
            </button>
            <div
              v-if="exportResponse !== null"
              class="mt-3"
            >
              <div v-if="exportResponse.success && exportResponse.url">
                <a
                  :href="exportResponse.url"
                  class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-green-600 px-3 py-2 text-sm
                    font-semibold text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500
                    disabled:pointer-events-none disabled:opacity-50"
                  download
                >
                  {{ t('download') }}
                </a>
              </div>
              <div v-else>
                <div class="text-red-500">
                  {{ exportResponse.error }}
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>
